import axios from "axios";
import { format } from "date-fns";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";

import { DataGrid } from "@material-ui/data-grid";
import { Box, Button, Grid, TextField, Typography } from "@material-ui/core";

import Layout from "../layouts/Layout";
import CopyToClipboardDataGridHeader from "../components/CopyToClipboardDataGridHeader";

const IMSIHealth = (props) => {
  const [loading, setLoading] = useState(false);
  const [imsis, setImsis] = useState([]);
  const [error, setError] = useState(undefined);

  const [imsisToSearch, setImsisToSearch] = useState([]);

  const [badImsi, setBadImsi] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [metaData, setMetaData] = useState({
    page: 1,
    page_size: 10,
    total: 1,
  });
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setCurrentPage(1);
    setPageSize(newPageSize);
  };

  const getImsiHealth = () => {
    setLoading(true);
    axios
      .post(
        "/imsi/health",
        imsisToSearch.map((imsi) => ({ imsi }))
      )
      .then((response) => {
        if (response.data.length) {
          setImsis(response.data);
        }
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getBadImsi = useCallback(() => {
    setLoading(true);
    axios
      .get(`/imsi/?page=${currentPage}&size=${pageSize}`)
      .then((response) => {
        if (response.data) {
          setBadImsi(response.data.items);
          setMetaData({
            page: response.data.page,
            page_size: response.data.page_size,
            total: response.data.total,
          });
        }
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [currentPage, pageSize, imsis]);

  const debouncedFetchData = debounce(() => {
    getBadImsi();
  }, 500);

  useEffect(() => {
    debouncedFetchData();
  }, [currentPage, pageSize, imsis]);

  const columns = [
    {
      field: "imsi",
      headerName: "IMSI",
      flex: 1,
      minWidth: 200,
      renderHeader: (params) => (
        <CopyToClipboardDataGridHeader params={params} prefix="'" />
      ),
    },
    {
      field: "message",
      headerName: "Status",
      flex: 1,
      minWidth: 200,
      renderHeader: (params) => (
        <CopyToClipboardDataGridHeader params={params} prefix="" />
      ),
    },
  ];

  const badImsiColumns = [
    ...columns,
    {
      field: "iccid",
      headerName: "ICCID",
      flex: 1,
      minWidth: 200,
      renderHeader: (params) => (
        <CopyToClipboardDataGridHeader params={params} prefix="'" />
      ),
    },
    {
      field: "last_updated_at",
      headerName: "Last Updated",
      flex: 1,
      renderCell: (params) => (
        <div>
          {params.row.created_at &&
            `${format(
              new Date(params.row.last_updated_at),
              "MMMM d, y hh:mm a"
            )}`}
        </div>
      ),
    },
  ];

  const downloadCSV = () => {
    if (!imsis.length) return;

    // Convert data to CSV format
    const headers = ["IMSI", "Status"];
    const rows = imsis.map((row) => [row.imsi, row.message]);
    const csvContent = [
      headers.join(","), // Add headers
      ...rows.map((row) => row.join(",")), // Add rows
    ].join("\n");

    // Create a Blob from the CSV content
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    // Create a link and trigger the download
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute("download", "IMSI_Health.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Layout
      loading={loading}
      error={error}
      pageTitle={props.title}
      containerMaxWidth="xl"
    >
      <Box pt={3}>
        <Box pt={2}>
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            spacing={5}
          >
            <Grid item xs={3}>
              <TextField
                id="imsi"
                label="IMSI(s)"
                variant="outlined"
                fullWidth
                onChange={(e) => setImsisToSearch(e.target.value.split("\n"))}
                minRows={5}
                multiline
                maxRows={5}
              />
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                disabled={!imsisToSearch.length}
                onClick={getImsiHealth}
              >
                Get Info
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                disabled={!imsis.length}
                onClick={downloadCSV}
              >
                Export
              </Button>
            </Grid>
          </Grid>
        </Box>
        {!imsis.length ? null : (
          <Box pt={2}>
            <DataGrid
              rows={imsis}
              columns={columns}
              disableSelectionOnClick
              autoHeight
              paginationMode="client"
              loading={loading}
              getRowId={(row) => row.imsi}
              density="compact"
            />
          </Box>
        )}
      </Box>
      {badImsi.length ? (
        <Box pt={2} mt={10}>
          <Typography variant="h5" align="left">
            Unhealthy IMSIs
            <DataGrid
              rows={badImsi}
              columns={badImsiColumns}
              disableSelectionOnClick
              autoHeight
              paginationMode="server"
              loading={loading}
              rowCount={metaData && metaData.total}
              page={metaData && metaData.page - 1}
              pageSize={metaData && metaData.page_size}
              onPageChange={(newPage) => handlePageChange(newPage + 1)}
              onPageSizeChange={handlePageSizeChange}
              rowsPerPageOptions={[5, 10, 20, 50, 100]}
              density="compact"
            />
          </Typography>
        </Box>
      ) : null}
    </Layout>
  );
};

IMSIHealth.defaultProps = {
  title: "IMSI Health",
};

export default IMSIHealth;
